import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { Info } from 'lucide-react';
import { metricsPath, isMetricsSubTabSlug } from '../../../routes/slugs';
import { markTourCompleteOnServer, checkAndMarkOnboardingComplete } from '../../../utils/onboarding';
import { MetricsResumenTab } from './metrics/MetricsResumenTab';
import { MetricsAsignacionTab } from './metrics/MetricsAsignacionTab';
import { MetricsAsignacionNTab } from './metrics/MetricsAsignacionNTab';
import { MetricsLiquidacionTab } from './metrics/MetricsLiquidacionTab';
import { MetricsInactividadTab } from './metrics/MetricsInactividadTab';
import { MetricsPsiPlusTab } from './metrics/MetricsPsiPlusTab';
import { getPsychologistMetrics, PsychologistMetrics } from '../../../hooks/api/psychologist/metrics';
import { usePsychologistContext } from '../../../contexts/PsychologistContext';
import { useAuth } from '../../../contexts/AuthContext';
import apiCoreAdapter from '../../../hooks/apiCoreAdapter';
import psiPlatformAdapter from '../../../hooks/psiPlatformAdapter';
import CircularProgress from '../../common/CircularProgress';
import { isImpersonated } from '../../../hooks/api/auth/tokenManager';
import { useIsPwaApp } from '../../../pwa/PwaModeContext';

// El ranking lo sirve psiplatform-be (/api/psychologist/metrics/ranking) con el id
// sacado del JWT. Este componente tenía su propia copia hardcodeada de la URL del
// webhook de Make, además de la que había en useRankingTransition y useNzJourneyStatus:
// tres lugares con la misma credencial, todos compilados dentro del bundle público.

type MetricsSubTab = 'resumen' | 'asignacion' | 'asignacion-inicial' | 'psi-plus' | 'liquidacion' | 'inactividad';

// Status reales de `psychologist_status` (verificado por Metabase): active, blocked,
// pending discharged, discharged.
const DISCHARGE_STATUS_NAMES = ['pending discharged', 'discharged'];

// "Desempeño" se saca por completo para todas las psicólogas (sin importar ranking/estado):
// nunca tuvo un contenido implementado detrás, quedaba en blanco al entrar.
const SUB_TABS: { id: MetricsSubTab; label: string }[] = [
  { id: 'resumen', label: 'Estadísticas' },
  { id: 'inactividad', label: 'Tasa de inactividad' },
  { id: 'asignacion', label: 'Asignaciones Generales' },
  { id: 'asignacion-inicial', label: 'Asignaciones Iniciales' },
  { id: 'psi-plus', label: 'Programa PSI+' },
  { id: 'liquidacion', label: 'Liquidación' },
];

function egresoAckKey(psychologistId: string) {
  return `psi-journey-egreso-ack-${psychologistId}`;
}

// Tabs que recién se vuelven visibles al graduarse de NZ (antes el journey las tapaba con
// "Camino inicial"). Se marcan con badge "Nuevo" hasta que la psicóloga entra a cada una.
const NUEVO_BADGE_TABS: MetricsSubTab[] = ['asignacion', 'inactividad', 'psi-plus'];

function nuevoTabBadgeSeenKey(psychologistId: string, tab: MetricsSubTab) {
  return `psi-metrics-tab-nuevo-badge-seen-${psychologistId}-${tab}`;
}

export const ProfileMetricsTab: React.FC = () => {
  // App mobile: sin onboarding guiado (solo web).
  const isPwaApp = useIsPwaApp();
  const navigate = useNavigate();
  const { subtab: subtabSlug } = useParams<{ subtab?: string }>();
  // Fuente de verdad: el slug de path (/profile/metricas o /profile/metricas/<subtab>). Un
  // segmento desconocido (typo) cae silenciosamente al default en vez de forzar un redirect.
  const activeTab: MetricsSubTab = subtabSlug && isMetricsSubTabSlug(subtabSlug) ? subtabSlug : 'resumen';
  const setActiveTab = useCallback((tab: MetricsSubTab) => {
    navigate(metricsPath(tab), { replace: true });
  }, [navigate]);
  const [metrics, setMetrics] = useState<PsychologistMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [blockades, setBlockades] = useState<Array<{ active: boolean; reason: { name: string } | null }>>([]);
  const [rankingActual, setRankingActual] = useState<string | null>(null);
  const [rankingAnterior, setRankingAnterior] = useState<string | null>(null);
  const [rankingLoading, setRankingLoading] = useState(true);
  const [rankingError, setRankingError] = useState(false);
  const [metricsError, setMetricsError] = useState(false);
  const { psychologist } = usePsychologistContext();
  const { psychologistId } = useAuth();

  const resolvedPsychologistId = psychologistId || psychologist?.id || '';
  const everHadProcesses = (metrics?.active_processes ?? 0) + (metrics?.inactive_processes ?? 0) > 0;
  const isDischargeStatusReal = DISCHARGE_STATUS_NAMES.includes((psychologist?.statusName ?? '').toLowerCase());
  const egresoAck = resolvedPsychologistId ? localStorage.getItem(egresoAckKey(resolvedPsychologistId)) === 'true' : false;

  // "Tu estadía en Psi Mammoliti" para NZ colapsa a 2 tabs (Camino inicial + Liquidación) en 3
  // situaciones, sin ningún endpoint de journey propio — todo inferido de datos que ya existen:
  // - NZ activa (ranking_category = 'NZ') → journey normal.
  // - NZ que le va mal y la dan de baja: pasa a ranking O + status pending discharged/discharged.
  //   Si tuvo procesos alguna vez → card de "Pending discharge" (no va a recibir asignaciones
  //   nuevas). Si nunca tuvo procesos, ya la expulsó AppShell antes de llegar a Perfil. Esta
  //   transición es definitiva — nunca "gradúa" ni hay popup de egreso — pero eso ya lo cubre el
  //   `!isDischargeStatusReal` de abajo (pasar a O desde NZ viene siempre acoplado a ese status).
  // - Recién graduada de NZ a Plantilla (ranking_anterior era 'NZ', ranking_actual ya no) y
  //   todavía no confirmó la card de Egreso → se le sigue mostrando el journey (con la card
  //   verde de egreso) hasta que confirma el popup; ahí se marca en localStorage y desde la
  //   próxima vez que entre ve las tabs normales (con badges "Nuevo" en las que recién se
  //   destapan: Asignaciones Generales, Tasa de Inactividad, Programa PSI+ y Home).
  const isJustGraduatedReal = rankingAnterior === 'NZ' && rankingActual !== 'NZ' && !isDischargeStatusReal && !egresoAck;

  // El status discharge/pending-discharge no es exclusivo de NZ: cualquier O puede pasar a baja
  // por otros motivos (inactividad, calidad, etc.) sin haber sido nunca NZ. Sin este chequeo, esa
  // O caía igual en la rama de journey NZ y veía Camino Inicial + el esquema de pagos de la Carta
  // Oferta NZ sin corresponderle.
  const wasEverNZ = rankingActual === 'NZ' || rankingAnterior === 'NZ';

  // `journeyMode` para el caso "pending-discharge" depende de `everHadProcesses`, que a su vez
  // depende de `metrics` (fetch separado del de ranking). Si solo esperáramos a `rankingLoading`,
  // una psicóloga en discharge que sí tuvo procesos vería primero las 4 tabs normales (con
  // `metrics` todavía en null → `everHadProcesses=false` → `journeyMode='none'`) y recién después
  // el colapso a Camino inicial cuando `metrics` resuelve — un flash confuso. Se espera también a
  // `loading` (metrics) en ese caso puntual, sin afectar a la mayoría de las psicólogas.
  const journeyModeLoading = rankingLoading || (isDischargeStatusReal && loading);

  type JourneyMode = 'active' | 'pending-discharge' | 'graduated' | 'none';
  const journeyMode: JourneyMode = isDischargeStatusReal
    ? (wasEverNZ && everHadProcesses ? 'pending-discharge' : 'none')
    : rankingActual === 'NZ'
      ? 'active'
      : isJustGraduatedReal
        ? 'graduated'
        : 'none';

  // "Camino Inicial" ya no vive acá (se mudó a la tab de nivel superior `/camino-inicial`,
  // ver AppShell + useNzJourneyStatus.journeyMode). Mientras la psicóloga está en ese journey,
  // Métricas colapsa a la única sub-tab que sigue siendo suya: Liquidación.
  const isInJourney = journeyMode !== 'none';

  // Mostrar solo Asignaciones Iniciales (fuera del journey NZ) si: ranking O con anterior N, o
  // ranking N — el caso NZ ya está cubierto por `isInJourney` arriba.
  const isNuevaEnO = (rankingActual === 'O' && rankingAnterior === 'N') || rankingActual === 'N';

  // "Estadísticas" solo para quien no es N (isNuevaEnO). El caso NZ / O-desde-NZ ya está
  // cubierto por `isInJourney` arriba, así que nunca llega a este branch.
  const hideEstadisticas = isNuevaEnO;

  const visibleTabs = useMemo(() => {
    if (isInJourney) {
      return SUB_TABS.filter((tab) => tab.id === 'liquidacion');
    }
    return SUB_TABS.filter((tab) => {
      if (tab.id === 'resumen' && hideEstadisticas) return false;
      if (isNuevaEnO) {
        return !['asignacion', 'psi-plus', 'inactividad'].includes(tab.id);
      }
      return tab.id !== 'asignacion-inicial';
    });
  }, [isNuevaEnO, isInJourney, hideEstadisticas]);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setMetricsError(false);
    getPsychologistMetrics()
      .then((res) => { if (!cancelled) setMetrics(res); })
      .catch(() => {
        if (cancelled) return;
        setMetrics(null);
        setMetricsError(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    apiCoreAdapter.psychologistBlockades()
      .then((res: { results: Array<{ active: boolean; reason: { name: string } | null }> }) => { if (!cancelled) setBlockades(res.results ?? []); })
      .catch(() => { if (!cancelled) setBlockades([]); });

    const psiId = psychologistId || psychologist?.id;
    if (!psiId) {
      setRankingLoading(false);
      return () => { cancelled = true; };
    }
    setRankingLoading(true);
    setRankingError(false);
    psiPlatformAdapter
      .metricsRanking()
      .then((data) => {
        if (cancelled) return;
        setRankingActual(data.ranking_actual);
        setRankingAnterior(data.ranking_anterior);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('No se pudo obtener ranking_actual/ranking_anterior de la psicóloga:', err);
        setRankingError(true);
      })
      .finally(() => { if (!cancelled) setRankingLoading(false); });

    return () => { cancelled = true; };
  }, [psychologistId, psychologist?.id]);

  // Si la tab activa queda oculta tras cargar el ranking, resetear al default correspondiente:
  // NZ (journey activo/pending-discharge/graduada) entra directo a "Liquidación" (única sub-tab
  // que le queda acá — "Camino inicial" ahora vive en `/camino-inicial`); el resto entra a
  // "Resumen" si está disponible, o si no a la primera tab visible (ej. quien tiene
  // "Estadísticas" oculta entra directo a "Asignaciones Generales").
  useEffect(() => {
    if (!visibleTabs.find((t) => t.id === activeTab)) {
      if (isInJourney) {
        setActiveTab('liquidacion');
      } else if (visibleTabs.find((t) => t.id === 'resumen')) {
        setActiveTab('resumen');
      } else if (visibleTabs[0]) {
        setActiveTab(visibleTabs[0].id);
      }
    }
  }, [visibleTabs, activeTab, isInJourney, setActiveTab]);

  // Badge "Nuevo" en Asignaciones Generales / Tasa de Inactividad / Programa PSI+ / Desempeño:
  // solo para quien se graduó de NZ (confirmó el popup de egreso), y solo hasta que entra a
  // cada una por separado.
  const [seenNuevoTabs, setSeenNuevoTabs] = useState<Set<MetricsSubTab>>(() => {
    if (!resolvedPsychologistId) return new Set();
    return new Set(NUEVO_BADGE_TABS.filter((t) => localStorage.getItem(nuevoTabBadgeSeenKey(resolvedPsychologistId, t)) === 'true'));
  });

  useEffect(() => {
    if (!resolvedPsychologistId) return;
    setSeenNuevoTabs(new Set(NUEVO_BADGE_TABS.filter((t) => localStorage.getItem(nuevoTabBadgeSeenKey(resolvedPsychologistId, t)) === 'true')));
  }, [resolvedPsychologistId]);

  useEffect(() => {
    if (!resolvedPsychologistId || !egresoAck) return;
    if (!NUEVO_BADGE_TABS.includes(activeTab) || seenNuevoTabs.has(activeTab)) return;
    localStorage.setItem(nuevoTabBadgeSeenKey(resolvedPsychologistId, activeTab), 'true');
    setSeenNuevoTabs((prev) => new Set(prev).add(activeTab));
  }, [activeTab, egresoAck, resolvedPsychologistId, seenNuevoTabs]);

  // Onboarding - Tu estadía en Psi Mammoliti (mismo patrón que "Sobre tu atención" y
  // "Vacaciones y Días off", pero implementado ACÁ y no en PsychologistProfile.tsx: este
  // componente es el único que conoce `visibleTabs`/`isInJourney` — el padre no.
  // No aplica para psicólogas en el journey de "Camino inicial" (NZ / recién egresada),
  // que ni siquiera ven esta sub-tab y ya tienen su propio sistema de guía (JourneyBot,
  // acordeón de recursos, popups de etapa).
  //
  // El tour es 100% dinámico: no todas las psicólogas ven las mismas sub-tabs (ver
  // `visibleTabs` más arriba — una psicóloga "N"/recién O solo ve Asignaciones Iniciales
  // + Liquidación, el resto ve hasta 5). Generamos un paso por cada tab que la sesión
  // actual efectivamente tiene visible, apuntando al título de esa tab y con un resumen
  // breve de su contenido — nunca se menciona una sección que a esta psicóloga no le
  // aplica, y nunca se entra al detalle interno de cada sub-tab (eso ya lo explica cada
  // una por sí misma).
  const [runMetricsTour, setRunMetricsTour] = useState<boolean>(false);
  const [metricsTourKey, setMetricsTourKey] = useState<number>(0);
  const [metricsStepIndex, setMetricsStepIndex] = useState<number>(0);

  const SECTION_SUMMARIES: Record<MetricsSubTab, string> = {
    resumen: 'Tus métricas principales: procesos activos, adherencia, sesiones próximas y tus gráficos de evolución mensual.',
    inactividad: 'Tu tasa de inactividad y el detalle de los procesos que la componen, para que puedas actuar a tiempo.',
    asignacion: 'Tu cupo de pacientes, cómo se calcula tu límite y el estado de tus asignaciones.',
    'asignacion-inicial': 'Tu camino de crecimiento inicial en la plataforma, con las etapas hacia tu próximo límite de pacientes.',
    'psi-plus': 'El Programa PSI+, nuestro esquema de incentivos, y tu progreso si calificás.',
    liquidacion: 'El historial de tus liquidaciones, y tus datos de cobro para gestionarlos.',
  };

  const metricsSteps: Step[] = useMemo(() => {
    const sectionSteps: Step[] = visibleTabs
      .map((tab) => ({
        target: `#metrics-tab-${tab.id}`,
        content: SECTION_SUMMARIES[tab.id],
        disableBeacon: true,
        spotlightPadding: 6,
        placement: 'bottom' as const,
      }));
    return [
      {
        target: '#metrics-subtabs-bar',
        content: 'Acá vas a encontrar toda la información de tu recorrido en Psi Mammoliti. Recorramos, una por una, las secciones que tenés disponibles.',
        disableBeacon: true,
        spotlightPadding: 6,
        placement: 'bottom' as const,
      },
      ...sectionSteps,
      {
        target: '#profile-metrics-section',
        content: 'Con esto ya tenés un pantallazo completo de tu estadía en Psi Mammoliti — volvé a cualquiera de estas secciones cuando quieras revisar tu progreso.',
        disableBeacon: true,
        spotlightPadding: 6,
        placement: 'top' as const,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs]);

  type MetricsJoyrideTooltipProps = {
    index: number;
    step?: { content?: React.ReactNode };
    backProps: Record<string, unknown>;
    primaryProps: Record<string, unknown>;
    tooltipProps: Record<string, unknown>;
  };

  const MetricsJoyrideTooltip: React.FC<MetricsJoyrideTooltipProps> = ({ index, step, backProps, primaryProps, tooltipProps }) => {
    const total = metricsSteps.length;
    const current = (Number(index) || 0) + 1;
    const isLast = current >= total;
    return (
      <div
        {...tooltipProps}
        style={{ background: '#fff', borderRadius: 8, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', maxWidth: 420, padding: '1rem 1rem 0.75rem 1rem', color: '#111827' }}
      >
        <div style={{ marginBottom: '0.75rem' }}>{step?.content}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', paddingTop: '0.25rem' }}>
          {index > 0 && (
            <button {...backProps} className="btn-outline" style={{ height: 32 }}>
              Atrás
            </button>
          )}
          <button
            {...primaryProps}
            className="btn-primary"
            style={{ height: 32, fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 14px', whiteSpace: 'nowrap', lineHeight: '32px' }}
          >
            {isLast ? 'Entendido' : `Continuar (${current} de ${total})`}
          </button>
        </div>
      </div>
    );
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const MetricsTooltipAny = MetricsJoyrideTooltip as any;

  const onMetricsTourCallback = (data: CallBackProps) => {
    const { status, type, action, index } = data as CallBackProps & { action?: string };
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRunMetricsTour(false);
      try { localStorage.setItem('psi-metrics-tour-done', '1'); } catch { /* noop */ }
      markTourCompleteOnServer('psi-metrics-tour-done');
      checkAndMarkOnboardingComplete();
      return;
    }
    if (type === 'step:after') {
      if (action === 'next') setMetricsStepIndex((index ?? 0) + 1);
      if (action === 'prev') setMetricsStepIndex(Math.max(0, (index ?? 0) - 1));
    }
  };

  // Auto-arranca la primera vez que se entra a la sub-tab default de ESTA psicóloga
  // (`visibleTabs[0]` — "Estadísticas" para la mayoría, pero "Asignaciones Iniciales"
  // para quien tiene "Estadísticas" oculta), una vez que el tour principal de Perfil ya
  // terminó. No corre mientras el journey de "Camino inicial" tapa esta vista.
  useEffect(() => {
    if (isPwaApp || journeyModeLoading || isInJourney) return;
    if (!visibleTabs[0] || activeTab !== visibleTabs[0].id) return;
    try {
      if (localStorage.getItem('psi-profile-tour-done') !== '1') return;
      if (localStorage.getItem('psi-metrics-tour-done') === '1') return;
    } catch {
      return;
    }
    const t = window.setTimeout(() => {
      setMetricsStepIndex(0);
      setRunMetricsTour(true);
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPwaApp, journeyModeLoading, isInJourney, activeTab, visibleTabs]);

  return (
    <div id="profile-metrics-section" className="flex flex-col gap-4">
      <Joyride
        key={metricsTourKey}
        steps={metricsSteps}
        run={runMetricsTour}
        stepIndex={metricsStepIndex}
        continuous
        scrollToFirstStep
        scrollOffset={300}
        disableOverlayClose
        disableCloseOnEsc
        spotlightClicks={false}
        disableScrolling={false}
        hideCloseButton
        styles={{
          options: { primaryColor: '#6D57BC', zIndex: 2000 },
          tooltip: { maxWidth: 420 },
          tooltipContent: { overflowWrap: 'anywhere', whiteSpace: 'normal' },
        }}
        floaterProps={{ offset: 12 }}
        locale={{ back: 'Atrás', close: 'Cerrar', last: 'Entendido', next: 'Continuar', skip: 'Saltar' }}
        tooltipComponent={MetricsTooltipAny}
        callback={onMetricsTourCallback}
      />
      {rankingError && isImpersonated() && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          (mensaje sólo disponible para impersonificaciones internas) No pudimos cargar esta
          sección. Si no ves el tab &quot;Camino Inicial&quot; y deberías, recargá la página.
        </div>
      )}
      {journeyModeLoading ? (
        <div className="flex justify-center py-10">
          <CircularProgress label="Cargando tu perfil..." />
        </div>
      ) : (
      <>
      {!isInJourney && !isPwaApp && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => { setMetricsTourKey((k) => k + 1); setMetricsStepIndex(0); setRunMetricsTour(true); }}
            title="Repasar guía de Tu estadía en Psi Mammoliti"
            aria-label="replay-metrics-onboarding"
            className="flex-shrink-0 inline-flex items-center gap-1 text-[0.75rem] font-normal text-[#8475b3] hover:text-[#6D57BC] bg-transparent border-0 cursor-pointer transition-colors"
          >
            <Info size={14} />
            Repasar guía de Tu estadía en Psi Mammoliti
          </button>
        </div>
      )}
      <div id="metrics-subtabs-bar" className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            id={`metrics-tab-${tab.id}`}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-violet-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {egresoAck && NUEVO_BADGE_TABS.includes(tab.id) && !seenNuevoTabs.has(tab.id) && (
              <span className="ml-1.5 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white align-middle">
                Nuevo
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'resumen' && <MetricsResumenTab metrics={metrics} loading={loading} />}
      {activeTab === 'asignacion' && (
        <MetricsAsignacionTab
          metrics={metrics}
          loading={loading}
          onGoToTasaInactividad={() => setActiveTab('inactividad')}
          blockades={blockades}
        />
      )}
      {activeTab === 'asignacion-inicial' && <MetricsAsignacionNTab metrics={metrics} loading={loading} />}
      {activeTab === 'psi-plus' && (
        <MetricsPsiPlusTab
          metrics={metrics}
          loading={loading}
          onNavigateToInactividad={() => setActiveTab('inactividad')}
        />
      )}
      {activeTab === 'liquidacion' && <MetricsLiquidacionTab isNZ={isInJourney} />}
      {activeTab === 'inactividad' && !isNuevaEnO && <MetricsInactividadTab onGoToAsignacion={() => setActiveTab('asignacion')} />}

      {/* Visible en cualquier sub-tab de "Tu estadía en Psi Mammoliti", no solo en Estadísticas. */}
      <div className="flex justify-center pt-4">
        <button
          type="button"
          onClick={() => navigate('/profile/farewell')}
          className="text-red-500 hover:text-red-700 underline text-sm font-medium transition-colors"
        >
          Quiero dejar de ser parte de Psi Mammoliti
        </button>
      </div>
      </>
      )}
    </div>
  );
};

export default ProfileMetricsTab;
